import * as XLSX from 'xlsx';

export type ImportedTable = {
  headers: string[];
  rows: Record<string, string>[];
  sheetNames?: string[];
  selectedSheet?: string;
};

export type GuardianMapping = {
  name: string;
  email: string;
  parentKey: string;
  relationship: string;
};

export type MappingSourceMode = '' | 'column' | 'assign_next_parent' | 'assign_next_family';

export type KeyMappingConfig = {
  mode: MappingSourceMode;
  header: string;
};

export type ImportMapping = {
  studentId: string;
  studentKey: string;
  studentName: string;
  firstName: string;
  lastName: string;
  grade: string;
  householdName: string;
  familyKey: KeyMappingConfig;
  familyName: string;
  isActive: string;
  specialsAssignments: string;
  guardians: GuardianMapping[];
};

export type ParsingOptions = {
  studentNameFormat: 'auto' | 'first_last' | 'last_first';
  guardianNameFormat: 'auto' | 'first_last' | 'last_first';
  trimWhitespace: boolean;
};

export type GuardianStage = {
  localId: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  relationship: string;
  parentKey: string;
  keyMode?: MappingSourceMode;
  existingParentId?: string;
  warning?: string;
};

export type ConflictMode = 'merge' | 'keep_existing' | 'overwrite';

export type StudentStage = {
  localId: string;
  sourceRowCount: number;
  studentId: string;
  studentKey: string;
  firstName: string;
  lastName: string;
  grade: string;
  householdName: string;
  familyKey: string;
  familyName: string;
  familyGroupKey: string;
  specialAssignments: string[];
  isActive: boolean;
  guardians: GuardianStage[];
  warnings: string[];
  errors: string[];
  duplicateSummary?: string[];
  conflictMode: ConflictMode;
};

export type ValidationSummary = {
  errors: number;
  warnings: number;
};

export const DEFAULT_PARSING_OPTIONS: ParsingOptions = {
  studentNameFormat: 'auto',
  guardianNameFormat: 'auto',
  trimWhitespace: true,
};

function norm(v: unknown) {
  return String(v ?? '').trim();
}

export function normalizeEmail(v: unknown) {
  return String(v ?? '').trim().toLowerCase();
}

function slug(v: string) {
  return v.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function parseCsvText(text: string): ImportedTable {
  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((l, idx, arr) => !(idx === arr.length - 1 && l.trim() === ''));

  if (!lines.length) return { headers: [], rows: [] };

  const rawRows = lines.map(parseCsvLine);
  const headers = rawRows[0].map((h) => h.trim());
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < rawRows.length; i++) {
    const values = rawRows[i];
    const row: Record<string, string> = {};
    for (let c = 0; c < headers.length; c++) {
      row[headers[c] || `col_${c}`] = String(values[c] ?? '').trim();
    }
    if (Object.values(row).some(Boolean)) rows.push(row);
  }
  return { headers, rows };
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else if (ch === '"') {
      inQuotes = true;
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

export async function parseSpreadsheetFile(file: File): Promise<ImportedTable> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    return parseCsvText(await file.text());
  }

  const bytes = await file.arrayBuffer();
  const workbook = XLSX.read(bytes, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const rowsRaw = XLSX.utils.sheet_to_json<Array<string | number | boolean | null>>(sheet, {
    header: 1,
    blankrows: false,
    defval: '',
  });

  const headers = ((rowsRaw[0] || []) as Array<string | number | boolean | null>).map(
    (v: string | number | boolean | null) => norm(v)
  );

  const rows: Record<string, string>[] = [];
  for (let i = 1; i < rowsRaw.length; i++) {
    const rowArr = (rowsRaw[i] || []) as Array<string | number | boolean | null>;
    const row: Record<string, string> = {};
    headers.forEach((h: string, idx: number) => {
      row[h || `col_${idx}`] = norm(rowArr[idx]);
    });
    if (Object.values(row).some(Boolean)) rows.push(row);
  }

  return { headers, rows, sheetNames: workbook.SheetNames, selectedSheet: sheetName };
}

function matchHeader(headers: string[], candidates: string[]): string {
  const bySlug = new Map(headers.map((h) => [slug(h), h]));
  for (const c of candidates) {
    const hit = bySlug.get(slug(c));
    if (hit) return hit;
  }
  return '';
}

function makeEmptyKeyConfig(): KeyMappingConfig {
  return { mode: '', header: '' };
}

export function suggestMapping(headers: string[]): ImportMapping {
  const guardians: GuardianMapping[] = [];
  for (let i = 1; i <= 6; i++) {
    const name = matchHeader(headers, [`Guardian${i} Name`, `Guardian ${i} Name`, `Parent${i} Name`, `Parent ${i} Name`, `Guardian ${i}`, `Parent ${i}`]);
    const email = matchHeader(headers, [`Guardian${i} Email`, `Guardian ${i} Email`, `Parent${i} Email`, `Parent ${i} Email`, `Guardian ${i} Email Address`, `Parent ${i} Email Address`]);
    const parentKey = matchHeader(headers, [`Guardian${i} Key`, `Parent${i} Key`, `Parent ${i} Key`, `Guardian ${i} Key`]);
    const relationship = matchHeader(headers, [`Guardian${i} Relationship`, `Guardian ${i} Relationship`, `Parent${i} Relationship`, `Parent ${i} Relationship`]);
    if (name || email || parentKey || relationship) guardians.push({ name, email, parentKey, relationship });
  }
  if (!guardians.length) guardians.push({ name: '', email: '', parentKey: '', relationship: '' });
  return {
    studentId: matchHeader(headers, ['Student Key', 'Student ID', 'StudentId', 'ID', 'Student Number']),
    studentKey: matchHeader(headers, ['Student Key', 'Student ID', 'StudentId', 'ID', 'Student Number']),
    studentName: matchHeader(headers, ['Student Name', 'Student', 'Full Name']),
    firstName: matchHeader(headers, ['Student First Name', 'First Name', 'First']),
    lastName: matchHeader(headers, ['Student Last Name', 'Last Name', 'Last']),
    grade: matchHeader(headers, ['Grade', 'Student Grade']),
    householdName: matchHeader(headers, ['Family Name', 'Household Name', 'Household']),
    familyKey: { mode: 'column', header: matchHeader(headers, ['Family Key', 'Family ID', 'FamilyId']) },
    familyName: matchHeader(headers, ['Family Name', 'Household Name']),
    isActive: matchHeader(headers, ['Is Active', 'Active']),
    specialsAssignments: matchHeader(headers, ['Special Assignments', 'Specials Assignments', 'Specials', 'Programs']),
    guardians,
  };
}

function splitNameByFormat(name: string, format: 'auto' | 'first_last' | 'last_first') {
  const clean = norm(name);
  if (!clean) return { firstName: '', lastName: '' };

  if (format === 'last_first') {
    if (clean.includes(',')) {
      const [last, ...rest] = clean.split(',');
      return { firstName: rest.join(',').trim(), lastName: last.trim() };
    }
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return { firstName: '', lastName: parts[0] };
    return { firstName: parts.slice(1).join(' '), lastName: parts[0] };
  }

  if (format === 'first_last') {
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
  }

  if (clean.includes(',')) {
    const [last, ...rest] = clean.split(',');
    return { firstName: rest.join(',').trim(), lastName: last.trim() };
  }

  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

export function splitName(name: string) {
  return splitNameByFormat(name, 'auto');
}

function boolFromString(v: string): boolean {
  const t = norm(v).toLowerCase();
  if (!t) return true;
  return !['false', '0', 'no', 'n', 'inactive'].includes(t);
}

function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

function familyGroupKey(row: Record<string, string>, mapping: ImportMapping, studentLastName: string, guardianEmails: string[]) {
  const explicit = norm(row[mapping.familyKey.header]);
  if (explicit) return `key:${explicit.toLowerCase()}`;

  const familyName = norm(row[mapping.familyName]);
  if (familyName) return `family:${familyName.toLowerCase()}`;

  const household = norm(row[mapping.householdName]);
  if (household) return `house:${household.toLowerCase()}`;

  const emails = unique(guardianEmails.filter(Boolean)).sort();
  if (emails.length) return `emails:${emails.join('|')}`;

  return `last:${studentLastName.toLowerCase()}`;
}

function parseSpecialAssignments(raw: string): string[] {
  if (!raw) return [];
  return unique(raw.split(/[,;|/]+/).map((x) => norm(x)).filter(Boolean));
}

export function buildStageRows(table: ImportedTable, mapping: ImportMapping, parsing: ParsingOptions = DEFAULT_PARSING_OPTIONS): StudentStage[] {
  const merged = new Map<string, StudentStage>();
  let seq = 1;

  for (const row of table.rows) {
    const studentKey = norm(row[mapping.studentKey] || row[mapping.studentId]);
    const grade = norm(row[mapping.grade]);
    let firstName = norm(row[mapping.firstName]);
    let lastName = norm(row[mapping.lastName]);
    const studentName = norm(row[mapping.studentName]);
    if ((!firstName || !lastName) && studentName) {
      const parts = splitNameByFormat(studentName, parsing.studentNameFormat);
      if (!firstName) firstName = parts.firstName;
      if (!lastName) lastName = parts.lastName;
    }

    const guardians: GuardianStage[] = [];
    const guardianEmails: string[] = [];
    mapping.guardians.forEach((g, idx) => {
      const name = norm(row[g.name]);
      const email = normalizeEmail(row[g.email]);
      const parentKey = '';
      const relationship = norm(row[g.relationship]) || 'Parent';
      if (!name && !email && !parentKey) return;
      const parts = splitNameByFormat(name, parsing.guardianNameFormat);
      guardians.push({
        localId: `g-${seq}-${idx}`,
        name,
        firstName: parts.firstName,
        lastName: parts.lastName,
        email,
        parentKey,
        relationship,
        keyMode: parentKey ? 'column' : '',
      });
      if (email) guardianEmails.push(email);
    });

    const recordId = studentKey || `${firstName}|${lastName}|${grade}`.toLowerCase();
    const familyName = norm(row[mapping.familyName] || row[mapping.householdName] || lastName);
    const familyKey = '';
    const groupKey = familyGroupKey(row, mapping, lastName || familyName, guardianEmails);
    const specials = parseSpecialAssignments(norm(row[mapping.specialsAssignments]));

    if (!merged.has(recordId)) {
      merged.set(recordId, {
        localId: `s-${seq++}`,
        sourceRowCount: 1,
        studentId: studentKey,
        studentKey,
        firstName,
        lastName,
        grade,
        householdName: norm(row[mapping.householdName] || row[mapping.familyName]),
        familyKey,
        familyName,
        familyGroupKey: groupKey,
        specialAssignments: specials,
        isActive: boolFromString(row[mapping.isActive]),
        guardians,
        warnings: [],
        errors: [],
        conflictMode: 'merge',
      });
    } else {
      const current = merged.get(recordId)!;
      current.sourceRowCount += 1;
      if (!current.studentId && studentKey) current.studentId = studentKey;
      if (!current.studentKey && studentKey) current.studentKey = studentKey;
      if (!current.firstName && firstName) current.firstName = firstName;
      if (!current.lastName && lastName) current.lastName = lastName;
      if (!current.grade && grade) current.grade = grade;
      if (!current.householdName && norm(row[mapping.householdName])) current.householdName = norm(row[mapping.householdName]);
      if (!current.familyKey && familyKey) current.familyKey = familyKey;
      if (!current.familyName && familyName) current.familyName = familyName;
      current.specialAssignments = unique([...current.specialAssignments, ...specials]);
      current.guardians.push(...guardians);
    }
  }

  const rows = Array.from(merged.values());
  rows.forEach((row) => {
    const seen = new Set<string>();
    row.guardians = row.guardians.filter((g) => {
      const dedupe = g.email || `${g.firstName}|${g.lastName}|${g.parentKey}`.toLowerCase();
      if (!dedupe) return false;
      if (seen.has(dedupe)) return false;
      seen.add(dedupe);
      return true;
    });
    row.errors = [];
    row.warnings = [];
    if (!row.studentKey) row.errors.push('Missing Student Key');
    if (!row.firstName || !row.lastName) row.errors.push('Missing student first/last name');
    if (!row.grade) row.errors.push('Missing grade');
    if (!row.familyName) row.warnings.push('Missing family name');
    if (!row.guardians.length) row.warnings.push('No guardians mapped');
    row.guardians.forEach((g) => {
      if (!g.relationship) g.relationship = 'Parent';
      if (!g.email) g.warning = 'Missing guardian email';
    });
  });
  return rows;
}

export function assignMissingFamilyKeys(rows: StudentStage[], start: number) {
  const nextRows = rows.map((r) => ({ ...r, guardians: r.guardians.map((g) => ({ ...g })) }));
  const groupToKey = new Map<string, string>();
  let next = start;

  const groupTokenForRow = (row: StudentStage) => {
    const familyName = norm(row.familyName || row.householdName);
    if (familyName) return `family:${familyName.toLowerCase()}`;

    const guardianEmails = unique(row.guardians.map((g) => normalizeEmail(g.email)).filter(Boolean)).sort();
    if (guardianEmails.length) return `emails:${guardianEmails.join('|')}`;

    return row.familyGroupKey || `last:${norm(row.lastName).toLowerCase()}`;
  };

  nextRows.forEach((row) => {
    const token = groupTokenForRow(row);
    if (row.familyKey) {
      groupToKey.set(token, row.familyKey);
      return;
    }

    const existing = groupToKey.get(token);
    if (existing) {
      row.familyKey = existing;
      return;
    }

    const key = `F${String(next).padStart(4, '0')}`;
    next += 1;
    groupToKey.set(token, key);
    row.familyKey = key;
  });

  return { rows: nextRows, nextNumber: next };
}

export function assignMissingParentKeys(rows: StudentStage[], start: number) {
  const nextRows = rows.map((r) => ({ ...r, guardians: r.guardians.map((g) => ({ ...g })) }));
  const refToKey = new Map<string, string>();
  let next = start;
  nextRows.forEach((row) => {
    row.guardians.forEach((g) => {
      if (g.parentKey) {
        const ref = g.email || `${g.firstName}|${g.lastName}|${row.familyKey}`.toLowerCase();
        if (ref) refToKey.set(ref, g.parentKey);
        return;
      }
      const ref = g.email || `${g.firstName}|${g.lastName}|${row.familyKey}`.toLowerCase();
      const existing = refToKey.get(ref);
      if (existing) {
        g.parentKey = existing;
        return;
      }
      const key = `P${String(next).padStart(4, '0')}`;
      next++;
      g.parentKey = key;
      refToKey.set(ref, key);
    });
  });
  return { rows: nextRows, nextNumber: next };
}


export function revalidateStageRows(rows: StudentStage[]): StudentStage[] {
  const nextRows = rows.map((r) => ({ ...r, guardians: r.guardians.map((g) => ({ ...g })) }));
  nextRows.forEach((row) => {
    row.errors = [];
    row.warnings = [];
    if (!row.studentKey) row.errors.push('Missing Student Key');
    if (!row.firstName || !row.lastName) row.errors.push('Missing student first/last name');
    if (!row.grade) row.errors.push('Missing grade');
    if (!row.familyName) row.warnings.push('Missing family name');
    if (!row.guardians.length) row.warnings.push('No guardians mapped');
    row.guardians.forEach((g, idx) => {
      if (!g.relationship) g.relationship = 'Parent';
      g.warning = undefined;
      const hasAnyGuardianData = !!(String(g.name || '').trim() || String(g.email || '').trim() || String(g.parentKey || '').trim());
      if (!hasAnyGuardianData) return;
      if (!g.email) g.warning = 'Missing guardian email';
    });
  });
  return nextRows;
}

export function summarizeRows(rows: StudentStage[]): ValidationSummary {
  return rows.reduce(
    (acc, row) => {
      acc.errors += row.errors.length;
      acc.warnings += row.warnings.length + row.guardians.filter((g) => !!g.warning).length;
      return acc;
    },
    { errors: 0, warnings: 0 }
  );
}

export function sampleValuesForHeader(table: ImportedTable | null, header: string, limit = 3): string[] {
  if (!table || !header) return [];
  const out: string[] = [];
  for (const row of table.rows) {
    const value = norm(row[header]);
    if (!value) continue;
    if (!out.includes(value)) out.push(value);
    if (out.length >= limit) break;
  }
  return out;
}

export function exampleForMappingField(table: ImportedTable | null, header: string): string {
  const samples = sampleValuesForHeader(table, header, 3);
  return samples.join(' • ');
}
